'use client';

import { motion, useScroll, useTransform } from 'motion/react';
import { useRef, useState } from 'react';

import { IconButton } from '@/decodo/components/atoms/IconButton';

import { Carousel } from '@/decodo/components/atoms/Carousel';
import NextImage from '@/decodo/components/atoms/Image';
import type { Swiper } from 'swiper/types';
import IconArrowLeft from '~/icons/arrow-left.svg';
import IconArrowRight from '~/icons/arrow-right.svg';
import { carouselConfig } from '../constants/carousel';
import { CarouselCard, CarouselCardProps } from './CarouselCard';

import { cn } from '@/lib/utils';
import { useMousePositionMotionValue } from '../hooks/useMousePositionMotionValue';
import styles from './HomepageCarousel.module.css';

export type HomepageCarouselProps = {
  slides: CarouselCardProps['slides'];
  delayFactor?: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
};

export function HomepageCarousel({
  slides,
  delayFactor,
  containerRef,
}: HomepageCarouselProps) {
  const [swiper, setSwiper] = useState<Swiper>();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const carouselContainerRef = useRef<HTMLDivElement | null>(null);
  const carouselCardRef = useRef<HTMLDivElement | null>(null);

  const { scrollYProgress } = useScroll({
    container: containerRef,
    target: carouselContainerRef,
    offset: ['start end', 'end start'],
  });

  const carouselScale = useTransform(
    scrollYProgress,
    [0, 0.2, 0.4],
    [1.3, 1.1, 1]
  );
  const carousePrevBtnX = useTransform(
    scrollYProgress,
    [0, 0.15, 0.4],
    [192, 192, 0]
  );
  const carouselNextBtnX = useTransform(
    scrollYProgress,
    [0, 0.15, 0.4],
    [-192, -192, 0]
  );
  const btnOpacity = useTransform(scrollYProgress, [0, 0.3, 0.4], [0, 0, 1]);

  const cardY = useTransform(
    scrollYProgress,
    [0, 0.4, 0.5, 0.55, 0.75, 1],
    ['80%', '5%', '0%', '5%', '35%', '65%']
  );

  const { springX: containerMouseX, springY: containerMouseY } =
    useMousePositionMotionValue(carouselContainerRef, { intensity: 2.5 });
  const { springX: cardMouseX, springY: cardMouseY } =
    useMousePositionMotionValue(carouselCardRef, { intensity: 1 });

  const carouselXOffset = useTransform(containerMouseX, (value) => -value);
  const carouselYOffset = useTransform(containerMouseY, (value) => -value);
  const carouselRotateX = useTransform(
    containerMouseX,
    (value) => `${value / 5}deg`
  );
  const carouselRotateY = useTransform(
    containerMouseY,
    (value) => `${-value / 5}deg`
  );
  const cardXOffset = useTransform(cardMouseX, (value) => value);
  const cardYOffset = useTransform(cardMouseY, (value) => value);
  const cardRotateX = useTransform(cardMouseX, (value) => `${value}deg`);
  const cardRotateY = useTransform(cardMouseY, (value) => `${value}deg`);

  return (
    <section className='hidden w-full grid-cols-[48px_1fr_48px] grid-rows-1 items-center justify-between gap-x-10 xl:grid'>
      <motion.div
        className='shrink-0'
        style={{ x: carousePrevBtnX, opacity: btnOpacity }}
      >
        <IconButton
          onClick={() => swiper?.slidePrev()}
          color='secondary'
          size='l'
          classNames={{
            root: 'dark:bg-gray-100 bg-gray-100 text-base-black rounded-full dark:hover:bg-gray-300  hover:bg-gray-200',
          }}
        >
          <IconArrowLeft className='h-8 w-8' />
        </IconButton>
      </motion.div>
      <motion.div
        ref={carouselContainerRef}
        className={cn(
          'transform-3d relative z-[1] aspect-[1104/600] max-h-[600px] max-w-full origin-top',
          styles.heroCarousel
        )}
        variants={{
          initial: { opacity: 0, y: '25%' },
          animate: { opacity: 1, y: 0 },
        }}
        transition={{ duration: 1.5, delay: delayFactor }}
        style={{
          scale: carouselScale,
        }}
      >
        <motion.div
          className='transform-3d h-full w-full'
          style={{
            x: carouselXOffset,
            y: carouselYOffset,
            rotateX: carouselRotateX,
            rotateY: carouselRotateY,
          }}
        >
          <Carousel
            config={{
              onSwiper: (swiper) => setSwiper(swiper),
              onSlideChange: (swiper) => setActiveSlideIndex(swiper.realIndex),
              ...carouselConfig,
            }}
            classNames={{
              root: 'rounded-[4px] h-full w-[92.75%] overflow-hidden',
            }}
            sideGradient={false}
            items={slides}
            renderItem={({ item, index }) => (
              <div className='aspect-[1024/600] max-h-[600px]'>
                <NextImage
                  {...slides[index].image}
                  objectFit='cover'
                  classNames={{
                    container:
                      'h-full full rounded-[4px] overflow-hidden border border-gray-200',
                    image: 'w-full h-full object-left-top',
                  }}
                />
              </div>
            )}
          />
        </motion.div>
        <motion.div
          ref={carouselCardRef}
          style={{
            transformStyle: 'preserve-3d',
            transformPerspective: 1000,
            rotateX: cardRotateX,
            rotateY: cardRotateY,
            y: cardYOffset,
            x: cardXOffset,
          }}
          className='absolute right-0 top-20 z-[1] max-w-[372px]'
        >
          <motion.div className='h-full w-full' style={{ y: cardY }}>
            <CarouselCard
              swiper={swiper}
              slides={slides}
              activeSlideIndex={activeSlideIndex}
            />
          </motion.div>
        </motion.div>
      </motion.div>
      <motion.div
        className='shrink-0'
        style={{ x: carouselNextBtnX, opacity: btnOpacity }}
      >
        <IconButton
          onClick={() => swiper?.slideNext()}
          color='secondary'
          size='l'
          classNames={{
            root: 'dark:bg-gray-100 bg-gray-100 text-base-black rounded-full dark:hover:bg-gray-300  hover:bg-gray-200',
          }}
        >
          <IconArrowRight className='h-8 w-8' />
        </IconButton>
      </motion.div>
    </section>
  );
}
